import React, { useState, type ReactNode } from 'react';
import { SettingOutlined, ShoppingCartOutlined, PlusOutlined } from '@ant-design/icons';
import ProductItemCard from '@components/shared/items/ProductItemCard';
import BundleItemGroup from '@components/shared/items/BundleItemGroup';
import OrderItemColumnsSettingModalImpl from './OrderItemColumnsSettingModal';
import { Button, Input, InputNumber, Row, Col, Form, type FormInstance } from 'antd';
import { toDayjsOrNull } from '@utils/dateTime';
import { useTranslation } from 'react-i18next';
import type { Bundle, BundleGroupEntry } from '@utils/orders/bundleExpansion';
import {
  ORDER_ITEM_SECONDARY_COLUMNS,
  type OrderItemSecondaryColumn,
} from '@utils/orders/orderItemSecondaryColumns';

const DEFAULT_AVAILABLE_COLUMNS: OrderItemSecondaryColumn[] = [...ORDER_ITEM_SECONDARY_COLUMNS];

interface FormListField {
  key: number | string;
  name: number;
}

interface InventoryType {
  id: string;
  name?: string;
  code?: string;
  [key: string]: unknown;
}

interface InventoryAttributeDefinition {
  id?: string;
  name: string;
  label?: string;
  data_type?: string;
  [key: string]: unknown;
}

interface OrderItemsFormProps {
  fields: FormListField[];
  add: (defaultValue?: Record<string, unknown>) => void;
  remove: (index: number | number[]) => void;
  form: FormInstance;
  productService: unknown;
  inventoryTypes: InventoryType[];
  merchantId?: string;
  enabledColumns?: string[];
  availableColumns?: string[];
  onUpdateColumns?: (columns: string[], merchantId?: string) => Promise<boolean> | boolean;
  showSettings?: boolean;
  inventoryAttributeDefinitions?: InventoryAttributeDefinition[];
  role?: string;
  mode?: 'input' | 'precise';
  inventoryService?: unknown;
  warehouseId?: string | null;
  bundleService?: unknown;
  onSelectBundle?: ((bundle: Bundle, cardIndex: number) => void) | null;
  searchMerchantId?: string | null;
  bundleGroups?: Record<string, BundleGroupEntry>;
  onBundleSetQuantityChange?: (bundleKey: string, val: number) => void;
  onRemoveBundle?: (bundleKey: string, remove: (index: number | number[]) => void) => void;
  /** Enable multi-select on the precise-mode inventory modal (Outbound Order). */
  multiSelectInventory?: boolean;
}

const OrderItemsForm: React.FC<OrderItemsFormProps> = ({
  fields,
  add,
  remove,
  form,
  productService,
  inventoryTypes,
  merchantId,
  enabledColumns = [],
  availableColumns = [],
  onUpdateColumns,
  showSettings = true,
  inventoryAttributeDefinitions = [],
  role = 'admin',
  mode = 'input',            // 'input' | 'precise'
  inventoryService = null,   // Precise mode 用的 inventoryService（admin/operator 不同）
  warehouseId = null,         // Precise mode 查庫存用的倉庫 ID
  // ── 統一搜尋（組合商品支援）──
  bundleService = null,
  onSelectBundle = null,
  searchMerchantId = null,
  // ── Bundle 分組渲染 ──
  bundleGroups = {},
  onBundleSetQuantityChange,
  onRemoveBundle,
  multiSelectInventory = false,
}) => {
  const { t } = useTranslation();
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);

  // OrderItemColumnsSettingModal 仍是 .jsx，destructure 推導不完整 →
  // 用 ComponentType<any> 包一層；放在 component body 內以避免 Vite HMR top-level TDZ。
  const OrderItemColumnsSettingModal = OrderItemColumnsSettingModalImpl as React.ComponentType<any>;
  // ProductItemCard 是 .tsx，但其 Props 不完整 — 暫以 any 包一層讓本檔可轉，
  // 後續應修 ProductItemCard 的 Props interface。
  const ProductItemCardLoose = ProductItemCard as unknown as React.ComponentType<any>;

  // Use fallback if availableColumns is empty
  const effectiveAvailableColumns = availableColumns.length > 0 ? availableColumns : DEFAULT_AVAILABLE_COLUMNS;

  type FieldConfigEntry =
    | { type: 'number'; label: string; props: Record<string, unknown> }
    | { type: 'textarea'; label: string; props: Record<string, unknown> }
    | { type: 'input'; label: string; props: Record<string, unknown> };

  const renderExtraFields = (
    name: number,
    restField: Record<string, unknown>,
    extraOptions: { disabled?: boolean } = {}
  ): ReactNode => {
    const { disabled: itemDisabled = false } = extraOptions;
    // Typed as Record<OrderItemSecondaryColumn, …> so the set of keys here is
    // compile-checked against ORDER_ITEM_SECONDARY_COLUMNS — adding a new optional
    // column means adding it to that const first, then to this map.
    const fieldConfig: Record<OrderItemSecondaryColumn, FieldConfigEntry> = {
      price: {
        type: 'number',
        label: t(`models.order_item.price`),
        props: { min: 0, precision: 2, style: { width: '100%' }, size: 'small', disabled: itemDisabled }
      },
      note: {
        type: 'textarea',
        label: t(`models.order.note`),
        props: { rows: 2, size: 'small', placeholder: t(`models.order.note`), disabled: itemDisabled }
      },
      identifier: {
        type: 'input',
        label: t(`models.order_item.identifier`),
        props: { size: 'small', disabled: itemDisabled }
      },
      original_price: {
        type: 'number',
        label: t(`models.order_item.original_price`),
        props: { min: 0, precision: 2, style: { width: '100%' }, size: 'small', disabled: itemDisabled }
      },
      internal_note: {
        type: 'textarea',
        label: t(`models.order_item.internal_note`),
        props: { rows: 2, size: 'small', placeholder: t(`models.order_item.internal_note`), disabled: itemDisabled }
      },
      cost: {
        type: 'number',
        label: t(`models.order_item.cost`),
        props: { min: 0, precision: 2, style: { width: '100%' }, size: 'small', disabled: itemDisabled }
      }
    };

    // Filter enabled columns that are extra fields
    // Custom Attributes are handled by ProductItemCard as Major Fields via inventoryAttributeDefinitions
    const visibleExtraColumns = enabledColumns.filter(
      (col): col is OrderItemSecondaryColumn =>
        (ORDER_ITEM_SECONDARY_COLUMNS as readonly string[]).includes(col),
    );

    if (visibleExtraColumns.length === 0) return null;

    return (
      <Row gutter={8} style={{ marginTop: 8 }}>
        {visibleExtraColumns.map(col => {
          const config = fieldConfig[col];
          return (
            <Col span={8} key={col}>
              <Form.Item
                {...restField}
                name={[name, col]}
                label={config.label}
              >
                {config.type === 'number' ? (
                  <InputNumber {...config.props} />
                ) : config.type === 'textarea' ? (
                  <Input.TextArea {...config.props} />
                ) : (
                  <Input {...config.props} />
                )}
              </Form.Item>
            </Col>
          );
        })}
      </Row>
    );
  };

  // Multi-select expansion: first pick replaces current card in place, rest append.
  // Applies whether the current card already had an inventory_id or not — the
  // entry point is always the "變更/選擇庫存" button, whose semantic is
  // "set this row's inventory", never "add more rows alongside".
  const expandInventoriesIntoItems = (
    currentFieldIndex: number,
    picks: Array<{ inventory: any; allocatedQuantity: number }>,
  ) => {
    if (!picks || picks.length === 0) return;
    const currentItem: any = form.getFieldValue(['order_items', currentFieldIndex]) || {};

    // Carry user-entered "extra" column values onto every produced row so that
    // splitting one line into multiple batches preserves the per-unit pricing /
    // note / identifier that applied to the original line. Driven by
    // ORDER_ITEM_SECONDARY_COLUMNS so adding a new column flows through automatically.
    const carriedExtras: Record<OrderItemSecondaryColumn, unknown> = Object.fromEntries(
      ORDER_ITEM_SECONDARY_COLUMNS.map((k) => [k, currentItem[k]]),
    ) as Record<OrderItemSecondaryColumn, unknown>;

    const buildItem = (pick: { inventory: any; allocatedQuantity: number }) => {
      const inv = pick.inventory;
      return {
        product_id: currentItem.product_id,
        product_sku: currentItem.product_sku,
        product_name: currentItem.product_name,
        product_validity_days: currentItem.product_validity_days,
        manufacturing_date_required: currentItem.manufacturing_date_required,
        batch_management_required: currentItem.batch_management_required,
        ...carriedExtras,
        inventory_id: inv.id,
        inventory_type_id: inv.inventory_type
          ? { value: inv.inventory_type.id, label: inv.inventory_type.name }
          : undefined,
        batch: inv.batch || null,
        // BE returns ISO-string dates; antd DatePicker requires Dayjs. See toDayjsOrNull.
        manufacturing_date: toDayjsOrNull(inv.manufacturing_date),
        expiration_date: toDayjsOrNull(inv.expiration_date),
        custom_attributes: inv.custom_attributes || {},
        quantity: pick.allocatedQuantity,
        _source_inventory: inv,
      };
    };

    const [first, ...rest] = picks;
    form.setFieldValue(['order_items', currentFieldIndex], {
      ...currentItem,
      ...buildItem(first),
    });
    rest.forEach((p) => add(buildItem(p)));
  };

  const computeExcludeIdsExcept = (currentFieldIndex: number): string[] => {
    const items: any[] = form.getFieldValue('order_items') || [];
    return items
      .map((it, idx) =>
        idx === currentFieldIndex ? null : it?.inventory_id?.value || it?.inventory_id,
      )
      .filter(Boolean) as string[];
  };

  const handleSettingsOk = async (columns: string[]) => {
    if (onUpdateColumns) {
      const success = await onUpdateColumns(columns, merchantId);
      if (success) {
        setSettingsModalVisible(false);
      }
    }
  };

  // ── 渲染 items（支援 bundle 分組）──
  const renderItems = () => {
    if (fields.length === 0) {
      return (
        <div style={{
          textAlign: 'center',
          padding: '48px 24px',
          border: '1px dashed #d9d9d9',
          borderRadius: '4px'
        }}>
          <ShoppingCartOutlined style={{ fontSize: 36, color: '#d9d9d9' }} />
          <div style={{ marginTop: 12, color: '#999' }}>
            {t(`shared.orders.form.no_products`)}
          </div>
          <div style={{ marginTop: 4, color: '#bbb', fontSize: 12 }}>
            {t(`shared.orders.form.sections.no_products_hint`)}
          </div>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => add({
              key: Date.now(),
              product_id: null,
              sku: '',
              name: '',
              inventory_type_id: null,
              quantity: 1,
              manufacturing_date: null,
              expiration_date: null,
              batch: '',
              note: '',
              price: null,
              identifier: '',
              original_price: null,
              cost: null,
              custom_attributes: {}
            })}
            style={{ marginTop: 16 }}
          >
            {t('shared.orders.form.add_product')}
          </Button>
        </div>
      );
    }

    const hasBundleGroups = bundleGroups && Object.keys(bundleGroups).length > 0;

    // 沒有 bundle 分組時，直接平鋪渲染
    if (!hasBundleGroups) {
      return fields.map(({ key, name, ...restField }) => {
        const itemValues = form.getFieldValue(['order_items', name]);
        const isExistingItem = !!itemValues?.id;
        const isUnknownProduct = !!itemValues?._is_unknown_product;
        const unknownProductId = isUnknownProduct ? itemValues?.product_id?.value || null : null;
        const shippedQuantity = Number(itemValues?.shipped_quantity ?? 0);

        return (
          <ProductItemCardLoose
            key={key}
            restField={restField}
            name={name}
            form={form}
            onRemove={remove}
            searchProductsService={productService}
            inventoryTypes={inventoryTypes}
            attributeDefinitions={inventoryAttributeDefinitions}
            renderExtraFields={renderExtraFields}
            listName="order_items"
            role={role}
            productReadOnly={isExistingItem}
            inventoryMode={mode}
            inventoryService={inventoryService}
            sourceWarehouseId={warehouseId}
            bundleService={bundleService}
            onSelectBundle={onSelectBundle}
            searchMerchantId={searchMerchantId}
            isUnknownProduct={isUnknownProduct}
            unknownProductId={unknownProductId}
            shippedQuantity={shippedQuantity}
            multiSelectInventory={multiSelectInventory && mode === 'precise'}
            onMultiSelectInventory={(picks: Array<{ inventory: any; allocatedQuantity: number }>) => expandInventoriesIntoItems(name, picks)}
            excludeInventoryIds={computeExcludeIdsExcept(name)}
            inventoryAttributesOptional
          />
        );
      });
    }

    // 有 bundle 分組時，按 bundle_key 歸組渲染
    const items = form.getFieldValue('order_items') || [];
    const renderedBundleKeys = new Set<string>();
    const elements: ReactNode[] = [];

    fields.forEach(({ key, name, ...restField }) => {
      const item = items[name];
      const bundleKey = item?.bundle_key;

      if (bundleKey && bundleGroups[bundleKey]) {
        // Bundle item: 只在第一次遇到此 bundleKey 時 render 整組
        if (renderedBundleKeys.has(bundleKey)) return;
        renderedBundleKeys.add(bundleKey);

        const group = bundleGroups[bundleKey];
        const bundleFields = fields.filter((f) => items[f.name]?.bundle_key === bundleKey);

        // 統一規則：「異動後的 order_items 必須完整涵蓋 shipped 部分」。
        // bundle group 不做整組鎖定 — 每個 component 各自依自己的 shipped_quantity 鎖
        // （由 ProductItemCard 處理）。BundleItemGroup 只需要：
        //   1. setQty 最小值 = 涵蓋所有已出貨數量所需的最小套數
        //      = max over components of ceil(component.shipped_quantity / per_set_qty)
        //   2. 若任一 component 已出貨 → 隱藏整組刪除按鈕（避免一次刪除帶走 shipped row）
        //   3. 顯示徽章告知出貨狀態（資訊 only，不限制操作）
        const componentShippedInfo = bundleFields.map((f) => {
          const v = items[f.name];
          return {
            shipped: Number(v?.shipped_quantity ?? 0),
            perSetQty: Number(v?.bundle_per_set_qty ?? 1) || 1,
          };
        });
        const bundleHasPartialShipped = componentShippedInfo.some((c) => c.shipped > 0);
        // 涵蓋 shipped 所需最小套數：每個 component 至少要 ceil(shipped / per_set_qty) 套
        const setQuantityMin = componentShippedInfo.reduce(
          (min, c) => Math.max(min, Math.ceil(c.shipped / c.perSetQty)),
          1,
        );
        // 已完整出貨的組數（資訊用，可為 0）
        const bundleShippedSets = componentShippedInfo.length > 0
          ? Math.min(
              ...componentShippedInfo.map((c) =>
                c.shipped > 0 ? Math.floor(c.shipped / c.perSetQty) : 0,
              ),
            )
          : 0;

        elements.push(
          <BundleItemGroup
            key={bundleKey}
            bundle={group.bundle}
            setQuantity={group.setQuantity}
            setQuantityMin={setQuantityMin}
            onSetQuantityChange={(val) => {
              // antd InputNumber 在使用者「全選刪除再打新值」的中間 state 會送 null。
              // 忽略 null，避免污染 bundleGroups.setQuantity（會把所有子項 quantity 算成 0）。
              if (val == null) return;
              onBundleSetQuantityChange?.(bundleKey, val);
            }}
            onRemove={() => onRemoveBundle?.(bundleKey, remove)}
            bundleShippedSets={bundleShippedSets}
            bundleHasPartialShipped={bundleHasPartialShipped}
          >
            {bundleFields.map(({ key: bKey, name: bName, ...bRestField }) => (
              <ProductItemCardLoose
                key={bKey}
                restField={bRestField}
                name={bName}
                form={form}
                onRemove={() => {}}
                searchProductsService={productService}
                inventoryTypes={inventoryTypes}
                attributeDefinitions={inventoryAttributeDefinitions}
                renderExtraFields={renderExtraFields}
                listName="order_items"
                productReadOnly
                quantityReadOnly
                role={role}
                inventoryMode={mode}
                inventoryService={inventoryService}
                sourceWarehouseId={warehouseId}
                // 統一規則：傳入 component 自己的 shipped_quantity，由 ProductItemCard
                // 依其 isShipped 判斷鎖定。shipped=0 的 sibling 不會被鎖（規則只保護
                // 已出貨部分；未出貨 sibling 可隨 bundle setQty 縮放）。
                shippedQuantity={Number(items[bName]?.shipped_quantity ?? 0)}
                inventoryAttributesOptional
              />
            ))}
          </BundleItemGroup>
        );
      } else {
        // 一般 item
        const itemValues = form.getFieldValue(['order_items', name]);
        const isExistingItem = !!itemValues?.id;
        const isUnknownProduct = !!itemValues?._is_unknown_product;
        const unknownProductId = isUnknownProduct ? itemValues?.product_id?.value || null : null;
        const shippedQuantity = Number(itemValues?.shipped_quantity ?? 0);

        elements.push(
          <ProductItemCardLoose
            key={key}
            restField={restField}
            name={name}
            form={form}
            onRemove={remove}
            searchProductsService={productService}
            inventoryTypes={inventoryTypes}
            attributeDefinitions={inventoryAttributeDefinitions}
            renderExtraFields={renderExtraFields}
            listName="order_items"
            role={role}
            productReadOnly={isExistingItem}
            inventoryMode={mode}
            inventoryService={inventoryService}
            sourceWarehouseId={warehouseId}
            bundleService={bundleService}
            onSelectBundle={onSelectBundle}
            searchMerchantId={searchMerchantId}
            isUnknownProduct={isUnknownProduct}
            unknownProductId={unknownProductId}
            shippedQuantity={shippedQuantity}
            multiSelectInventory={multiSelectInventory && mode === 'precise'}
            onMultiSelectInventory={(picks: Array<{ inventory: any; allocatedQuantity: number }>) => expandInventoriesIntoItems(name, picks)}
            excludeInventoryIds={computeExcludeIdsExcept(name)}
            inventoryAttributesOptional
          />
        );
      }
    });

    return elements;
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {fields && fields.length > 0 && (
            <span style={{ color: '#666' }}>
              {t(`shared.orders.form.total_items`, { count: fields.length })}
            </span>
          )}
        </div>

        {showSettings && onUpdateColumns && (
          <Button
            icon={<SettingOutlined />}
            onClick={() => setSettingsModalVisible(true)}
          >
            {t(`shared.orders.form.configure_columns`)}
          </Button>
        )}
      </div>

      {renderItems()}

      <OrderItemColumnsSettingModal
        visible={settingsModalVisible}
        onOk={handleSettingsOk}
        onCancel={() => setSettingsModalVisible(false)}
        availableColumns={effectiveAvailableColumns}
        initialEnabledColumns={enabledColumns}
      />
    </div>
  );
};

export default OrderItemsForm;
